import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, FileText, Search, Scale, Gavel, MessageSquare, Shield, BookOpen, FileEdit, GitCompare, Mail, Clipboard, Mic, Bot, Zap, PlusCircle } from 'lucide-react';
import MatrixBackground from '@/MatrixBackground';
import MarketingHeader from '@/components/MarketingHeader';
import usePageSEO from '@/hooks/usePageSEO';

const categories = [
  {
    name: 'Document Intelligence',
    description: 'Analyse, review, and understand legal documents with AI that reads like a senior associate.',
    tools: [
      {
        name: 'Document Review',
        tagline: 'Comprehensive analysis of any legal document',
        description: 'Upload any contract, agreement, or legal filing and receive a structured multi-part analysis covering key terms, obligations, risks, and recommendations. Designed to surface what matters most, fast.',
        icon: FileText,
      },
      {
        name: 'Contract Review',
        tagline: 'Rapid risk and issue scanning',
        description: 'Purpose-built for speed. Scans contracts to flag problematic clauses, unusual terms, and potential liabilities — so you know where to focus your attention before you read a single page.',
        icon: Shield,
      },
      {
        name: 'Inline Review & Editor',
        tagline: 'AI-annotated document markup',
        description: 'Review documents with intelligent inline annotations highlighting risks, ambiguities, and suggested improvements. Edit directly within the tool and re-analyse your changes in real time.',
        icon: FileEdit,
      },
      {
        name: 'Compare Documents',
        tagline: 'Side-by-side clause-level comparison',
        description: 'Upload two versions of any document and instantly see what changed — clause by clause. Ideal for tracking negotiations, lease variations, and contract amendments.',
        icon: GitCompare,
      },
      {
        name: 'Legal Summarisation',
        tagline: 'Distil lengthy documents into concise overviews',
        description: 'Transform 50-page judgments, policy wordings, or board minutes into structured, digestible summaries. Extracts the key points so you can brief clients or colleagues in minutes.',
        icon: BookOpen,
      },
    ],
  },
  {
    name: 'Research & Analysis',
    description: 'Go deeper. Research case law, conduct due diligence, and prepare for depositions with AI-powered analysis.',
    tools: [
      {
        name: 'Legal Research',
        tagline: 'AI-assisted case law and statute research',
        description: 'Conduct in-depth research across case law, statutory provisions, and legal principles. Lawra identifies relevant authorities and summarises their application to your specific legal question.',
        icon: Search,
      },
      {
        name: 'Due Diligence',
        tagline: 'Systematic document analysis for transactions',
        description: 'Process large volumes of transaction documents to extract key findings, flag compliance gaps, and identify material risks. Built for the pace and pressure of M&A and corporate work.',
        icon: Scale,
      },
      {
        name: 'Deposition & Witness Preparation',
        tagline: 'Generate targeted questions from witness materials',
        description: 'Upload witness statements and related evidence. Lawra analyses the material to generate comprehensive deposition questions, identify inconsistencies, and suggest lines of cross-examination.',
        icon: Gavel,
      },
    ],
  },
  {
    name: 'Drafting & Communication',
    description: 'Generate documents, draft correspondence, and respond to clients — all tailored to your practice area.',
    tools: [
      {
        name: 'Document Generation',
        tagline: 'AI-drafted legal documents from your instructions',
        description: 'Describe what you need and Lawra generates professional legal documents tailored to your jurisdiction and practice area. From client care letters to consent orders and beyond.',
        icon: FileText,
      },
      {
        name: 'Email Response Assistant',
        tagline: 'Professional legal correspondence in seconds',
        description: 'Paste incoming correspondence and receive contextually appropriate draft responses — matching the tone and formality required for tribunal communications, client updates, and opposing counsel.',
        icon: Mail,
      },
      {
        name: 'Debt Recovery Tools',
        tagline: 'Compliant recovery letters at scale',
        description: 'Generate Letters Before Action, follow-up demands, and settlement proposals tailored to each debt and debtor profile. Built to comply with Pre-Action Protocol and regulatory guidelines.',
        icon: Shield,
      },
    ],
  },
  {
    name: 'Workflow & Productivity',
    description: 'Tools that fit into your daily routine — from AI chat to voice notes and task management.',
    tools: [
      {
        name: 'Ask Lawra AI',
        tagline: 'Conversational AI legal assistant',
        description: 'Ask any legal question and receive instant, informed guidance. Lawra draws on legal knowledge to help you think through problems, check your reasoning, and explore options.',
        icon: Bot,
      },
      {
        name: 'Instruction & Action',
        tagline: 'Turn client instructions into structured outputs',
        description: 'Capture client instructions in any format and transform them into structured, actionable task lists and document drafts. Bridges the gap between what clients say and what needs to happen.',
        icon: Clipboard,
      },
      {
        name: 'Notetaker',
        tagline: 'AI-powered legal note capture',
        description: 'Record meetings, calls, or dictate notes and receive clean, structured legal notes. Captures the detail you need without the admin burden of manual note-taking.',
        icon: Mic,
      },
    ],
  },
];

const sectorTools = [
  { sector: 'Corporate & Commercial', tools: 'Contract Review, Due Diligence, Document Comparison, Document Generation' },
  { sector: 'Litigation & Dispute Resolution', tools: 'Deposition Prep, Legal Research, Document Review, Legal Summarisation' },
  { sector: 'Property & Real Estate', tools: 'Document Comparison, Contract Review, Legal Summarisation, Document Generation' },
  { sector: 'Family Law', tools: 'Document Generation, Email Response, Legal Summarisation, Ask Lawra' },
  { sector: 'Criminal Defence', tools: 'Legal Research, Deposition Prep, Legal Summarisation, Ask Lawra' },
  { sector: 'Employment Law', tools: 'Email Response, Document Review, Contract Review, Document Generation' },
  { sector: 'Immigration', tools: 'Ask Lawra, Legal Research, Document Generation, Email Response' },
  { sector: 'Insurance & Financial Services', tools: 'Legal Summarisation, Contract Review, Due Diligence, Document Comparison' },
  { sector: 'Debt Recovery', tools: 'Debt Recovery Tools, Document Generation, Email Response, Ask Lawra' },
  { sector: 'Technology & IP', tools: 'Contract Review, Due Diligence, Document Comparison, Legal Research' },
];

const Tools = () => {
  const navigate = useNavigate();

  usePageSEO({
    title: 'AI Legal Tools for Lawyers - Contract Review, Research, Drafting & More | Lawra AI',
    description: 'Explore Lawra AI\'s suite of AI-powered legal tools. Document review, contract analysis, legal research, due diligence, document generation, email drafting, and more. Built exclusively for solicitors and lawyers.',
    path: '/tools',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Lawra AI",
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web",
      "description": "AI-powered legal tools for solicitors and lawyers.",
      "featureList": categories.flatMap(c => c.tools.map(t => t.name)),
    },
  });

  return (
    <div className="landing-page">
      <MatrixBackground />
      <MarketingHeader />

      {/* Hero */}
      <section className="pricing-hero" data-testid="tools-hero">
        <div className="pricing-hero-inner">
          <p className="uc-hero-label">Tools</p>
          <h1 className="uc-hero-title">Every Tool a Lawyer Needs</h1>
          <p className="uc-hero-subtitle">
            A growing suite of AI-powered tools designed around how legal professionals actually work. Each one built to save time, reduce risk, and deliver better outcomes.
          </p>
        </div>
      </section>

      {/* Tool Categories */}
      {categories.map((cat, ci) => (
        <section key={ci} className="tools-category" data-testid={`tools-cat-${ci}`}>
          <div className="tools-category-inner">
            <div className="tools-cat-header">
              <h2 className="tools-cat-title">{cat.name}</h2>
              <p className="tools-cat-desc">{cat.description}</p>
            </div>
            <div className="tools-cat-grid">
              {cat.tools.map((tool, ti) => {
                const Icon = tool.icon;
                return (
                  <div key={ti} className="tools-page-card" data-testid={`tool-card-${ci}-${ti}`}>
                    <div className="tools-page-card-icon">
                      <Icon size={22} />
                    </div>
                    <div className="tools-page-card-content">
                      <h3>{tool.name}</h3>
                      <p className="tools-page-tagline">{tool.tagline}</p>
                      <p className="tools-page-desc">{tool.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      ))}

      {/* Sector Coverage */}
      <section className="tools-sectors" data-testid="tools-sectors">
        <div className="tools-sectors-inner">
          <h2 className="tools-cat-title" style={{ textAlign: 'center', marginBottom: '0.75rem' }}>Sector Coverage</h2>
          <p className="tools-cat-desc" style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
            Different areas of law demand different tools. Lawra AI is built to serve them all — with more sector-specific capabilities being added continuously.
          </p>
          <div className="tools-sector-grid">
            {sectorTools.map((s, i) => (
              <div key={i} className="tools-sector-card" data-testid={`sector-${i}`}>
                <h4>{s.sector}</h4>
                <p>{s.tools}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* More Coming */}
      <section className="tools-more" data-testid="tools-more">
        <div className="tools-more-inner">
          <PlusCircle size={36} className="tools-more-icon" />
          <h2 className="tools-cat-title" style={{ textAlign: 'center' }}>And More Being Built Every Day</h2>
          <p className="tools-more-desc">
            What you see today is just the beginning. New tools, new capabilities, and deeper sector-specific features are being developed and shipped continuously — driven by feedback from the lawyers who use Lawra AI every day.
          </p>
          <p className="tools-more-desc">
            Have a tool idea? We want to hear it.
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap', marginTop: '1.5rem' }}>
            <Button
              className="try-now-btn"
              size="lg"
              data-testid="tools-cta-trial"
              onClick={async () => {
                try {
                  const { auth } = await import('../firebase');
                  if (auth.currentUser) navigate('/dashboard');
                  else navigate('/onboarding');
                } catch { navigate('/onboarding'); }
              }}
            >
              Try the Tools for Free
            </Button>
            <Button variant="ghost" className="use-cases-nav-link" size="lg" onClick={() => navigate('/pricing')} data-testid="tools-cta-pricing">
              View Pricing <ArrowRight size={16} />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Tools;
